import React, { useState, useRef, useLayoutEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  Trash2,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  Plus,
  Pencil,
  Check,
  X,
  GripVertical,
} from "lucide-react";
import { v4 as uuid } from "uuid";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Button from "./common/ui/button/Button";
import Label from "./common/form/Label";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import type { SignerRole } from "../types/request";
import { Select } from "./common/ui/select/Select";
import { SignaturePlace } from "@secure-caseworker/shared";
import { generateKeyFromLabel } from "../views/RequestTemplates/components/SignerRolesEditor";
import { usePdfManagement } from "../contexts/PdfManagementContext";

// Use the worker URL via import.meta.url
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url,
).href;

// Signer colors - exported for consistency with SignerRolesEditor
export const SIGNER_COLORS = [
  "#3B82F6", // blue
  "#10B981", // green
  "#F59E0B", // amber
  "#EF4444", // red
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#14B8A6", // teal
  "#F97316", // orange
  "#6366F1", // indigo
  "#84CC16", // lime
];

interface SortableSignerItemProps {
  signer: SignerRole;
  index: number;
  children: React.ReactNode;
}

const SortableSignerItem: React.FC<SortableSignerItemProps> = ({
  signer,
  index,
  children,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: signer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <button
        type="button"
        className="cursor-grab text-gray-400 hover:text-gray-600"
        {...attributes}
        {...listeners}
        title="Drag to reorder"
      >
        <GripVertical size={14} />
      </button>

      <div
        className="h-4 w-4 flex-shrink-0 rounded"
        style={{
          backgroundColor: SIGNER_COLORS[index % SIGNER_COLORS.length],
        }}
      />

      {children}
    </div>
  );
};

export const PdfSignatureBoxDrawer: React.FC = () => {
  const {
    signerRoles,
    setSignerRoles,
    getSignaturePlaces,
    handleUpdateSignaturePlaces,
    getPdfUrl,
  } = usePdfManagement();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [selectedBox, setSelectedBox] = useState<string | null>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [tempBox, setTempBox] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const signaturePlaces = getSignaturePlaces();
  const pdfUrl = getPdfUrl();
  // Tool state
  const [selectedFieldType, setSelectedFieldType] =
    useState<SignaturePlace["type"]>("signature");

  const [selectedSignerKey, setSelectedSignerKey] = useState<string>(
    signerRoles[0]?.key || "",
  );

  // State for adding new signer
  const [isAddingNewSigner, setIsAddingNewSigner] = useState(false);
  const [newSignerLabel, setNewSignerLabel] = useState("");
  const [newSignerRequired, setNewSignerRequired] = useState(true);

  // State for editing existing signer label
  const [editingSignerId, setEditingSignerId] = useState<string | null>(null);
  const [editingSignerLabel, setEditingSignerLabel] = useState("");

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Helper to create a new signer with unique key
  const createNewSigner = (label: string, required: boolean): SignerRole => {
    const existingKeys = signerRoles.map((s) => s.key);
    let key = generateKeyFromLabel(label.trim());
    if (!key || existingKeys.includes(key)) {
      key = `${key || "signer"}_${signerRoles.length + 1}`;
    }
    return {
      id: uuid(),
      key,
      label: label.trim(),
      signing_order: signerRoles.length + 1,
      required,
    };
  };

  const handleAddNewSigner = () => {
    if (!newSignerLabel.trim()) return;

    const newSigner = createNewSigner(newSignerLabel, newSignerRequired);
    const updated = normalizeSignerOrder([...signerRoles, newSigner]);
    setSignerRoles(updated);
    setSelectedSignerKey(newSigner.key);

    setNewSignerLabel("");
    setNewSignerRequired(true);
    setIsAddingNewSigner(false);
  };

  const handleOrderEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = signerRoles.findIndex((s) => s.id === active.id);
    const newIndex = signerRoles.findIndex((s) => s.id === over.id);

    const reordered = arrayMove(signerRoles, oldIndex, newIndex);
    setSignerRoles(normalizeSignerOrder(reordered));
  };

  const handleUpdateSignerLabel = (signerId: string) => {
    if (!editingSignerLabel.trim()) return;

    const signer = signerRoles.find((s) => s.id === signerId);
    if (!signer) return;

    const oldKey = signer.key;
    const newLabel = editingSignerLabel.trim();
    const newKey = generateKeyFromLabel(newLabel) || signer.key;

    // Update the signer
    const updatedSigners = signerRoles.map((s) => {
      if (s.id === signerId) {
        return {
          ...s,
          label: newLabel,
          key: newKey,
        };
      }
      return s;
    });

    setSignerRoles(updatedSigners);

    // Update all placement boxes associated with this signer
    const updatedPlaces = signaturePlaces.map((place) => {
      if (place.template_signer_key === oldKey) {
        return {
          ...place,
          template_signer_key: newKey,
          label: `${
            place.type.charAt(0).toUpperCase() + place.type.slice(1)
          } - ${newLabel}`,
        };
      }
      return place;
    });
    handleUpdateSignaturePlaces(updatedPlaces);

    // Update selected signer key if it was the one being edited
    if (selectedSignerKey === oldKey) {
      setSelectedSignerKey(newKey);
    }

    setEditingSignerId(null);
    setEditingSignerLabel("");
  };

  const handleDeleteSigner = (signerId: string) => {
    if (signerRoles.length <= 1) return;

    const signerToDelete = signerRoles.find((s) => s.id === signerId);
    if (!signerToDelete) return;

    // Remove the signer
    const updatedSigners = signerRoles.filter((s) => s.id !== signerId);
    setSignerRoles(updatedSigners);

    // Remove all placement boxes associated with this signer
    const updatedPlaces = signaturePlaces.filter(
      (place) => place.template_signer_key !== signerToDelete.key,
    );
    handleUpdateSignaturePlaces(updatedPlaces);

    // If the deleted signer was selected, select the first remaining signer
    if (selectedSignerKey === signerToDelete.key && updatedSigners.length > 0) {
      setSelectedSignerKey(updatedSigners[0].key);
    }
  };

  const startEditingSigner = (signer: SignerRole) => {
    setEditingSignerId(signer.id);
    setEditingSignerLabel(signer.label);
  };

  const cancelEditingSigner = () => {
    setEditingSignerId(null);
    setEditingSignerLabel("");
  };

  const getSignerIndex = (signerKey: string): number => {
    const index = signerRoles.findIndex((s) => s.key === signerKey);
    return index >= 0 ? index : 0;
  };
  const normalizeSignerOrder = (signers: SignerRole[]): SignerRole[] =>
    signers
      .slice()
      .sort((a, b) => (a.signing_order ?? 0) - (b.signing_order ?? 0))
      .map((signer, index) => ({
        ...signer,
        signing_order: index + 1,
      }));

  // Get currently selected signer role
  const selectedSigner = signerRoles.find((s) => s.key === selectedSignerKey);

  const canvasRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState<number>(0);
  const [pageHeight, setPageHeight] = useState<number>(0);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(Math.floor(entry.contentRect.width));
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const onPageLoadSuccess = (_page: any) => {
    const el = canvasRef.current?.querySelector("canvas");
    if (!el) return;

    setPageWidth(el.clientWidth);
    setPageHeight(el.clientHeight);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (selectedBox) {
      setSelectedBox(null);
      return;
    }

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setIsDrawing(true);
    setDrawStart({ x, y });
    setTempBox({ x, y, width: 0, height: 0 });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDrawing || !drawStart) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    const width = currentX - drawStart.x;
    const height = currentY - drawStart.y;

    setTempBox({
      x: width < 0 ? currentX : drawStart.x,
      y: height < 0 ? currentY : drawStart.y,
      width: Math.abs(width),
      height: Math.abs(height),
    });
  };

  const handleMouseUp = () => {
    if (
      !isDrawing ||
      !tempBox ||
      !drawStart ||
      tempBox.width < 20 ||
      tempBox.height < 20 ||
      !selectedSigner
    ) {
      setIsDrawing(false);
      setTempBox(null);
      setDrawStart(null);
      return;
    }

    // Convert pixels → normalized (0–1)
    const xNorm = tempBox.x / pageWidth;
    const yNorm = tempBox.y / pageHeight;
    const widthNorm = tempBox.width / pageWidth;
    const heightNorm = tempBox.height / pageHeight;

    const newPlace: SignaturePlace = {
      id: `place_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: selectedFieldType,
      page: currentPage,
      x: xNorm,
      y: yNorm,
      width: widthNorm,
      height: heightNorm,
      template_signer_key: selectedSignerKey,
      label: `${
        selectedFieldType.charAt(0).toUpperCase() + selectedFieldType.slice(1)
      } - ${selectedSigner.label}`,
    };

    handleUpdateSignaturePlaces([...signaturePlaces, newPlace]);

    setIsDrawing(false);
    setTempBox(null);
    setDrawStart(null);
  };

  const handleDeleteBox = (id: string) => {
    handleUpdateSignaturePlaces(
      signaturePlaces.filter((place) => place.id !== id),
    );
    setSelectedBox(null);
  };

  const handleBoxClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedBox(id);
  };

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 2.0));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));
  const nextPage = () => setCurrentPage((prev) => Math.min(prev + 1, numPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  // Filter places for current page
  const currentPagePlaces = signaturePlaces.filter(
    (place) => place.page === currentPage,
  );

  const zoomedWidth = containerWidth * scale;
  const orderedSigners = [...signerRoles].sort((a, b) => {
    const orderA = a.signing_order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.signing_order ?? Number.MAX_SAFE_INTEGER;

    return orderA - orderB;
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-6">
        {/* Toolbar Sidebar */}
        <div className="min-w-[200px] w-64 space-y-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
          <div>
            <Label>Field Type</Label>
            <Select
              value={selectedFieldType}
              options={[
                { label: "Signature", value: "signature" },
                { label: "Initials", value: "initials" },
                { label: "Date", value: "date" },
                // TODO: We need to support sequential signing first
                // { label: "Text", value: "text" },
              ]}
              onChange={(value) =>
                setSelectedFieldType(value as SignaturePlace["type"])
              }
            />
          </div>

          <div>
            <Label>Assign to Signer</Label>
            <div className="space-y-2">
              <Select
                value={selectedSignerKey}
                options={signerRoles.map((role) => ({
                  label: role.label,
                  value: role.key,
                }))}
                onChange={(value) => setSelectedSignerKey(value as string)}
              />

              <>
                {isAddingNewSigner ? (
                  <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      New Signer
                    </div>
                    <input
                      type="text"
                      value={newSignerLabel}
                      onChange={(e) => setNewSignerLabel(e.target.value)}
                      placeholder="e.g., Client, Spouse, Attorney"
                      className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddNewSigner();
                        if (e.key === "Escape") {
                          setIsAddingNewSigner(false);
                          setNewSignerLabel("");
                          setNewSignerRequired(true);
                        }
                      }}
                    />
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newSignerRequired}
                        onChange={(e) => setNewSignerRequired(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        Required
                      </span>
                    </label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleAddNewSigner}
                        disabled={!newSignerLabel.trim()}
                        startIcon={<Check size={14} />}
                      >
                        Add
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsAddingNewSigner(false);
                          setNewSignerLabel("");
                          setNewSignerRequired(true);
                        }}
                        startIcon={<X size={14} />}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsAddingNewSigner(true)}
                    className="flex w-full items-center justify-center gap-1 rounded border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 transition-colors hover:border-brand-500 hover:text-brand-500 dark:border-gray-600 dark:text-gray-400 dark:hover:border-brand-400 dark:hover:text-brand-400"
                  >
                    <Plus size={14} />
                    <span>New Signer</span>
                  </button>
                )}
              </>
            </div>
          </div>

          <div className="pt-2">
            <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Signers
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleOrderEnd}
            >
              <SortableContext
                items={orderedSigners.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1">
                  {orderedSigners.map((signer, index) => (
                    <SortableSignerItem
                      key={signer.id}
                      signer={signer}
                      index={index}
                    >
                      {editingSignerId === signer.id ? (
                        <div className="flex flex-1 items-center gap-1">
                          <input
                            type="text"
                            value={editingSignerLabel}
                            onChange={(e) =>
                              setEditingSignerLabel(e.target.value)
                            }
                            className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter")
                                handleUpdateSignerLabel(signer.id);
                              if (e.key === "Escape") cancelEditingSigner();
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleUpdateSignerLabel(signer.id)}
                            className="p-0.5 text-green-600 hover:text-green-700"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditingSigner}
                            className="p-0.5 text-gray-400 hover:text-gray-600"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="group flex flex-1 items-center justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            {signer.label}
                            {!signer.required && (
                              <span className="ml-1 text-xs text-gray-400">
                                (optional)
                              </span>
                            )}
                          </span>

                          <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                              type="button"
                              onClick={() => startEditingSigner(signer)}
                              className="p-0.5 text-gray-400 hover:text-gray-600"
                              title="Edit label"
                            >
                              <Pencil size={12} />
                            </button>
                            {signerRoles.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleDeleteSigner(signer.id)}
                                className="p-0.5 text-gray-400 hover:text-red-500"
                                title="Delete signer and all associated fields"
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </SortableSignerItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p className="mb-2 font-medium">Instructions:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Select field type and signer</li>
                <li>Click and drag on the PDF to create a box</li>
                <li>Click a box to select it</li>
                <li>Use delete button to remove</li>
              </ol>
            </div>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 min-w-0">
          {/* Controls */}
          <div className="mb-4 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={prevPage}
                disabled={currentPage <= 1}
                startIcon={<ChevronLeft size={16} />}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {currentPage} of {numPages}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={nextPage}
                disabled={currentPage >= numPages}
                endIcon={<ChevronRight size={16} />}
              >
                Next
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={zoomOut}
                disabled={scale <= 0.5}
                startIcon={<ZoomOut size={16} />}
              >
                Zoom Out
              </Button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {Math.round(scale * 100)}%
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={zoomIn}
                disabled={scale >= 2.0}
                startIcon={<ZoomIn size={16} />}
              >
                Zoom In
              </Button>
            </div>
          </div>

          {/* PDF Canvas */}
          <div
            ref={containerRef}
            className="relative overflow-auto max-w-full rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
          >
            <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess}>
              <div
                ref={canvasRef}
                className="relative inline-block cursor-crosshair"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={() => {
                  if (isDrawing) {
                    setIsDrawing(false);
                    setTempBox(null);
                    setDrawStart(null);
                  }
                }}
              >
                <Page
                  pageNumber={currentPage}
                  width={zoomedWidth}
                  onLoadSuccess={onPageLoadSuccess}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />

                {/* Existing signature boxes */}
                {currentPagePlaces.map((place) => {
                  const x = place.x * pageWidth;
                  const y = place.y * pageHeight;
                  const width = place.width * pageWidth;
                  const height = place.height * pageHeight;
                  const signerIndex = getSignerIndex(place.template_signer_key);
                  const color =
                    SIGNER_COLORS[signerIndex % SIGNER_COLORS.length];
                  const isSelected = selectedBox === place.id;

                  return (
                    <div
                      key={place.id}
                      className="absolute cursor-pointer"
                      style={{
                        left: `${x}px`,
                        top: `${y}px`,
                        width: `${width}px`,
                        height: `${height}px`,
                        border: `2px ${
                          isSelected ? "solid" : "dashed"
                        } ${color}`,
                        backgroundColor: `${color}20`,
                        boxShadow: isSelected ? `0 0 0 3px ${color}40` : "none",
                      }}
                      onClick={(e) => handleBoxClick(e, place.id)}
                    >
                      <div
                        className="flex h-full items-center justify-center text-xs font-medium"
                        style={{ color }}
                      >
                        {place.label}
                      </div>
                      {isSelected && (
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBox(place.id);
                          }}
                          className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1 text-white shadow-lg hover:bg-red-600"
                          title="Delete field"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  );
                })}

                {/* Temporary drawing box */}
                {isDrawing && tempBox && (
                  <div
                    className="pointer-events-none absolute"
                    style={{
                      left: `${tempBox.x}px`,
                      top: `${tempBox.y}px`,
                      width: `${tempBox.width}px`,
                      height: `${tempBox.height}px`,
                      border: `2px dashed ${
                        SIGNER_COLORS[
                          getSignerIndex(selectedSignerKey) %
                            SIGNER_COLORS.length
                        ]
                      }`,
                      backgroundColor: `${
                        SIGNER_COLORS[
                          getSignerIndex(selectedSignerKey) %
                            SIGNER_COLORS.length
                        ]
                      }20`,
                    }}
                  />
                )}
              </div>
            </Document>
          </div>

          {/* Summary */}
          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Signature Fields Summary
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total fields: {signaturePlaces.length}
              {signaturePlaces.length > 0 && (
                <span className="ml-4">
                  Signers with fields:{" "}
                  {
                    new Set(signaturePlaces.map((p) => p.template_signer_key))
                      .size
                  }{" "}
                  / {signerRoles.length}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
